import { Component, OnInit, Input, OnDestroy, ChangeDetectorRef, ViewChild, ElementRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatAngularModule } from '../../modules/mat-angular.module';
import { MatDialog } from '@angular/material/dialog';
import { NgxChartsModule } from '@swimlane/ngx-charts';
import { BarLineComboChartComponent } from '../../charts/bar-line-combo-chart/bar-line-combo-chart.component';
import { CommonPerformanceTableComponent } from '../../components/common-performance-table/common-performance-table.component';
import { GenericTemplateCommonService } from '../../components/template-ai-common/services/generic-template-common.service';
import { Subscription } from 'rxjs';
import { GenericAiVisibilityComponent } from '../../components/generic-ai-visibility/generic-ai-visibility.component';
import { SemiCircleComponent } from '../../charts/semi-circle/semi-circle.component';
import { CommonSimpleTableComponent, TableColumn, TableConfig } from '../../components/common-simple-table/common-simple-table.component';
import { BulkBackgroundTabsProgress, TaskStatus } from '../../models/bulk-search.models';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TopPromptByPlatformDialogComponent } from '../../../dialogs/top-prompt-by-platform-dialog/top-prompt-by-platform-dialog.component';
import { TopPromptByPlatformItem, PLATFORM_LABELS } from '../../models/top-prompt-by-platform.model';
import { environment } from '../../../../environments/environment';


@Component({
  selector: 'app-generic-overview',
  standalone: true,
  imports: [
    CommonModule,
    MatAngularModule,
    NgxChartsModule,
    BarLineComboChartComponent,
    CommonPerformanceTableComponent,
    GenericAiVisibilityComponent,
    SemiCircleComponent,
    CommonSimpleTableComponent,
  ],
  templateUrl: './generic-overview.component.html',
  styleUrl: './generic-overview.component.scss'
})
export class GenericOverviewComponent implements OnInit, OnDestroy {
  @Input() selectedEntities: any[] = [];

  overviewData: any = null;

  // Chart properties
  chartData: any[] = [];

  // Bar Line Combo Chart properties (similar to generic-ai-citations)
  results: any[] = [];
  lineChart: any[] = [];
  /** Bar series brand name from competitor_analysis when composite_score is empty (BE optimization) */
  barSeriesBrandName: string | null = null;

  /** Metric keys and display labels for competitor_analysis "series-by-brand" format (no x_axis) */
  private static readonly COMPETITOR_METRIC_KEYS = [
    'reputation', 'market_presence', 'customer_satisfaction', 'innovation', 'financial_performance', 'industry_influence'
  ] as const;
  private static readonly COMPETITOR_METRIC_LABELS = [
    'Brand Reputation', 'Market Presence', 'Customer Satisfaction', 'Innovation Score', 'Financial Performance', 'Industry Influence'
  ];
  /** For each metric index, possible keys (component then API style) */
  private static readonly METRIC_KEYS_BY_INDEX: readonly (readonly string[])[] = [
    ['reputation', 'brand_reputation'],
    ['market_presence'],
    ['customer_satisfaction'],
    ['innovation', 'innovation_score'],
    ['financial_performance'],
    ['industry_influence']
  ];

  // Composite Score Card Data - Dynamic from API
  compositeScore: number = 0;

  // Color schemes for charts - different colors for each bar
  comboBarScheme: any = {
    name: 'singleLightBlue',
    selectable: true,
    group: 'ordinal',
    domain: ['#1976d2']
  };

  lineChartScheme: any = {
    name: 'coolthree',
    selectable: true,
    group: 'ordinal',
    domain: ['#01579b', '#7aa3e5', '#a8385d', '#00bfa5']
  };

  // Responsive view dimensions
  view: [number, number] = [600, 400];

  // Table properties
  tableColumns: any[] = [];
  tableRows: any[] = [];

  // Evidence sources
  evidenceSources: any[] = [];

  // LLM Comparison data
  llmComparisonData: any[] = [];
  llmComparisonMetadata: any = null;

  // LLM Comparison Metrics progress tracking
  llmComparisonMetricsProgress: TaskStatus | null = null;
  isLLMComparisonMetricsLoading = false;
  llmComparisonMetricsTaskId: string | null = null;

  // Cited pages filter/sort state
  selectedModelFilter: 'all' | 'chatgpt' | 'gemini' | 'ai_overview' | 'ai_mode' = 'all';
  sortByDomain = false;

  // Top Domains: selected domain for showing cited pages (mirrors AI Citation tab)
  selectedDomainForPages: string | null = null;
  /** When false, progress text below loader is hidden (set on reload click) */
  showProgressBelowLoader = true;

  /** Silent header: remaining bulk tabs (e.g. 2/9) after overview */
  bulkBackgroundProgress: BulkBackgroundTabsProgress | null = null;

  @ViewChild('citedPagesTable', { static: false }) citedPagesTable!: ElementRef;
  @ViewChild('overviewPrintable', { static: false }) overviewPrintable!: ElementRef;

  citedPagesColumns: TableColumn[] = [
    { key: 'url', label: 'Cited Page URL', sortable: false, align: 'left', width: '80%' },
    { key: 'models', label: 'Models', sortable: false, align: 'left', width: '20%' }
  ];

  // Subscriptions
  private subscriptions: Subscription[] = [];

  constructor(
    private templateCommonService: GenericTemplateCommonService,
    private cdr: ChangeDetectorRef,
    private dialog: MatDialog,
    private snackBar: MatSnackBar
  ) { }

  ngOnInit(): void {
    // Subscribe to analysis response from GenericTemplateCommonService
    const analysisSub = this.templateCommonService.analysisResponse$.subscribe((response: any) => {
      if (response) {
        // Process the response data instead of calling onReload()
        this.overviewData = response;
        // Ensure metadata exists with safe defaults
        if (!this.overviewData.metadata) {
          this.overviewData.metadata = {};
        }
        // Ensure competitors is an array
        if (!Array.isArray(this.overviewData.metadata.competitors)) {
          this.overviewData.metadata.competitors = [];
        }
        this.enrichMetadataFromProject();
        
        // Extract evidence sources from Tavily
        this.evidenceSources = this.transformSourcesData(
          response.structured_data?.evidence_sources || 
          response.evidence_sources || 
          []
        );
        
        this.prepareChartData(response);
        // Extract LLM comparison data - check both root and structured_data
        this.extractLLMComparisonData(response);
        // Also check if llm_comparison is at root level
        if (!this.llmComparisonData || this.llmComparisonData.length === 0) {
          this.extractLLMComparisonData(this.overviewData);
        }
        this.cdr.detectChanges();
      }
    });

    // Subscribe to LLM comparison metrics progress
    const llmProgressSub = this.templateCommonService.llmComparisonMetricsProgress$.subscribe((progress: TaskStatus | null) => {
      this.llmComparisonMetricsProgress = progress;
      if (progress) {
        this.isLLMComparisonMetricsLoading = progress.status === 'progress' || progress.status === 'pending' || progress.status === 'in_progress';
        console.log(`📊 LLM Comparison Metrics Progress: ${progress.progress_percentage.toFixed(1)}% - ${progress.current_tab || 'Processing...'}`);
      } else {
        // Only set loading to false if we also don't have a task ID
        // This ensures stop button shows even if progress data isn't available yet
        if (!this.llmComparisonMetricsTaskId) {
          this.isLLMComparisonMetricsLoading = false;
        }
      }
      this.cdr.detectChanges();
    });

    // Subscribe to LLM comparison metrics task ID
    const llmTaskIdSub = this.templateCommonService.llmComparisonMetricsTaskId$.subscribe((taskId: string | null) => {
      this.llmComparisonMetricsTaskId = taskId;
      if (taskId) {
        this.isLLMComparisonMetricsLoading = true;
      } else {
        if (!this.llmComparisonMetricsProgress) {
          this.isLLMComparisonMetricsLoading = false;
        }
        this.showProgressBelowLoader = true; // Reset so progress shows on next run
      }
      this.cdr.detectChanges();
    });

    const bgBulkSub = this.templateCommonService.backgroundTabsProgress$.subscribe((p) => {
      this.bulkBackgroundProgress = p;
      this.cdr.markForCheck();
    });
    this.subscriptions.push(analysisSub, llmProgressSub, llmTaskIdSub, bgBulkSub);

    // Check for active LLM comparison metrics task on component init
    this.checkForActiveLLMComparisonMetricsTask();

    // Load data when component initializes
    this.loadOverviewData();
  }

  /** Fill metadata.industry and metadata.category from project when missing (for Primary Industry / Primary Category in Analysis Details). */
  private enrichMetadataFromProject(): void {
    if (!this.overviewData?.metadata) return;
    const project = this.templateCommonService.getCurrentProjectData();
    if (!project) return;
    if (!this.overviewData.metadata.industry && project.industry) {
      this.overviewData.metadata.industry = project.industry;
    }
    if (!this.overviewData.metadata.category && project.category) {
      this.overviewData.metadata.category = project.category;
    }
  }

  /**
   * Check for active LLM comparison metrics task when component loads
   */
  private checkForActiveLLMComparisonMetricsTask(): void {
    // Check if there's an active task by getting current status
    const currentTaskId = this.templateCommonService.getCurrentLLMComparisonMetricsTaskId();
    if (currentTaskId) {
      // Task ID exists, start polling
      console.log(`🔄 Found active LLM comparison metrics task: ${currentTaskId}`);
      this.templateCommonService.startLLMComparisonMetricsPolling(currentTaskId);
      return;
    }

    // Check backend for active task
    this.templateCommonService.checkAndResumeActiveLLMComparisonMetricsTask();
  }

  ngOnDestroy(): void {
    console.log('🗑️ Overview: Destroying component and clearing memory...');
    
    // Unsubscribe from all subscriptions
    this.subscriptions.forEach(sub => sub.unsubscribe());
    
    // Don't clear service-level cache - data should persist across tab switches
    // The service manages the data and tabs subscribe to observables
    
    // Clear all local data references
    this.overviewData = null;
    this.chartData = [];
    this.results = [];
    this.lineChart = [];
    this.compositeScore = 0;
    this.tableColumns = [];
    this.tableRows = [];
    
    console.log('✅ Overview: Component destroyed and memory cleared');
  }

  loadOverviewData(): void {
    // Check if we already have a response (data might have been loaded before component initialized)
    const currentResponse = this.templateCommonService.getCurrentAnalysisResponse();
    if (currentResponse) {
      console.log('Overview: Found existing response, processing immediately');
      this.overviewData = currentResponse;
      // Ensure metadata exists with safe defaults
      if (!this.overviewData.metadata) {
        this.overviewData.metadata = {};
      }
      // Ensure competitors is an array
      if (!Array.isArray(this.overviewData.metadata.competitors)) {
        this.overviewData.metadata.competitors = [];
      }
      this.enrichMetadataFromProject();
      this.prepareChartData(currentResponse);
      // Extract LLM comparison data - check both root and structured_data
      this.extractLLMComparisonData(currentResponse);
      // Also check if llm_comparison is at root level
      if (!this.llmComparisonData || this.llmComparisonData.length === 0) {
        this.extractLLMComparisonData(this.overviewData);
      }
      this.cdr.detectChanges();
    } else {
      // No data yet - subscription in ngOnInit() will handle it when data arrives
      console.log('Overview: No data yet, waiting for subscription to receive data');
    }
  }

  onStopBackgroundBulk(): void {
    this.templateCommonService.cancelBackgroundBulkTabs();
    this.snackBar.open('Stopped loading remaining tabs.', 'Close', { duration: 4000 });
  }

  // Method to trigger reload from parent component
  /** Open AI Citations tab and scroll to Sources by domain (full table). */
  viewAllTopDomainsInAiCitations(): void {
    this.templateCommonService.requestNavigateToTab('ai-citations', 'ai-citations-sources-by-domain');
  }

  onReload(): void {
    // Reload only the overview tab (runs overview on backend and updates tab in place, no full project reload)
    this.templateCommonService.triggerTabReload('overview', true);
  }

  async onReloadOtherTabs(): Promise<void> {
    const result = await this.templateCommonService.reloadOtherTabsBulk();
    if (result.started) {
      this.snackBar.open('Started loading remaining tabs in background.', 'Close', { duration: 4000 });
      return;
    }
    this.snackBar.open(result.message || 'Failed to start loading other tabs.', 'Close', { duration: 5000 });
  }

  /** Show Reload button only in non-production (same condition as Category Analysis). */
  get showOverviewReload(): boolean {
    return !environment.production;
  }

  /** True when overview-prompts-build ran but AI Visibility rows are still missing. */
  isPromptsBuildOverview(): boolean {
    if (this.overviewData?.llm_comparison?.metadata?.source !== 'overview_prompts_build') {
      return false;
    }
    const cd = this.overviewData?.llm_comparison?.comparison_data;
    return !Array.isArray(cd) || cd.length === 0;
  }

  /** Normalize overview so chart/table data is read from root or structured_data (e.g. history may nest under structured_data). */
  private normalizeOverviewData(data: any): any {
    if (!data) return data;
    const competitorAnalysis = data.competitor_analysis ?? data.structured_data?.competitor_analysis;
    const performanceTable = data.performance_table ?? data.structured_data?.performance_table;
    const compositeScore = data.composite_score ?? data.structured_data?.composite_score;
    if (competitorAnalysis != null || performanceTable != null || compositeScore != null) {
      return {
        ...data,
        competitor_analysis: competitorAnalysis ?? data.competitor_analysis,
        performance_table: performanceTable ?? data.performance_table,
        composite_score: compositeScore ?? data.composite_score
      };
    }
    return data;
  }

  private prepareChartData(data: any): void {
    const d = this.normalizeOverviewData(data);

    if (d?.composite_score != null && typeof d.composite_score.value === 'number') {
      this.compositeScore = Math.round(d.composite_score.value);
    }
    if (d?.competitor_analysis?.chart_data) {
      const chartData = d.competitor_analysis.chart_data;

      // Transform chart data to ngx-charts format
      this.chartData = chartData.labels.map((label: string, index: number) => {
        const dataset = chartData.datasets[0]; // Get first dataset
        return {
          name: label,
          value: dataset.data[index] || 0
        };
      });

    } else if (d?.competitor_analysis?.series) {
      // Handle series data from competitor_analysis
      this.prepareSeriesChartData(d.competitor_analysis);
    } else {
      // Fallback: use performance table data
      if (d?.performance_table && d.performance_table.length > 0) {
        this.chartData = d.performance_table.map((item: any) => ({
          name: item.brand,
          value: item.brand_reputation || 0
        }));
      } else {
        this.chartData = [];
      }
    }

    // Prepare combo chart data (uses same normalized data)
    this.prepareComboChartData(d);

    // Prepare table data
    this.prepareTableData(d);
  }

  /** True when competitor_analysis uses series-by-brand format (name/brand + metric keys), false when it uses x_axis + series[].data */
  private isCompetitorAnalysisSeriesByBrandFormat(competitorAnalysis: any): boolean {
    const first = competitorAnalysis?.series?.[0];
    if (!first) return false;
    const hasBrandName = typeof (first.brand ?? first.name) === 'string';
    const hasMetric = GenericOverviewComponent.METRIC_KEYS_BY_INDEX.some((keys) =>
      keys.some((k) => first[k] != null)
    );
    return !!hasBrandName && hasMetric;
  }

  /** Get metric value from a series-by-brand item by label index (supports both reputation/brand_reputation and innovation/innovation_score). */
  private getSeriesByBrandMetricValue(item: any, index: number): number {
    const keys = GenericOverviewComponent.METRIC_KEYS_BY_INDEX[index];
    if (!keys) return 0;
    for (const k of keys) {
      if (item[k] != null) return Number(item[k]);
    }
    return 0;
  }

  private prepareSeriesChartData(competitorAnalysis: any): void {
    if (!competitorAnalysis?.series?.length) {
      this.chartData = [];
      return;
    }

    // New format: series = [{ name/brand, brand_reputation/reputation, ... }], no x_axis
    if (this.isCompetitorAnalysisSeriesByBrandFormat(competitorAnalysis)) {
      const firstSeries = competitorAnalysis.series[0];
      this.chartData = GenericOverviewComponent.COMPETITOR_METRIC_LABELS.map((label, index) => ({
        name: label,
        value: this.getSeriesByBrandMetricValue(firstSeries, index)
      }));
      return;
    }

    // Legacy format: x_axis + series[].data
    const xAxis = competitorAnalysis.x_axis || [];
    const primaryBrand = competitorAnalysis.series.find((s: any) => s.type === 'bar');
    const seriesToUse = primaryBrand ?? competitorAnalysis.series[0];
    this.chartData = xAxis.map((label: string, index: number) => ({
      name: label,
      value: (seriesToUse?.data && seriesToUse.data[index]) ?? 0
    }));
  }

  private prepareComboChartData(data: any): void {
    const d = this.normalizeOverviewData(data);
    if (!d) {
      this.results = [];
      this.lineChart = [];
      return;
    }

    // Prefer competitor_analysis.series when performance_table is empty (e.g. when BE returns empty performance_table)
    const competitorAnalysis = d.competitor_analysis;
    if (competitorAnalysis?.series?.length && (!d.performance_table || d.performance_table.length === 0)) {
      const xAxisLabels = GenericOverviewComponent.COMPETITOR_METRIC_LABELS;

      // New format: series = [{ name/brand, brand_reputation/reputation, ... }] — first = bar, rest = lines
      if (this.isCompetitorAnalysisSeriesByBrandFormat(competitorAnalysis)) {
        const [barBrand, ...lineBrands] = competitorAnalysis.series;
        this.barSeriesBrandName = (barBrand?.name ?? barBrand?.brand) ?? null;
        this.results = xAxisLabels.map((label, index) => ({
          name: label,
          value: this.getSeriesByBrandMetricValue(barBrand, index)
        }));
        this.lineChart = lineBrands.map((s: any) => ({
          name: (s.name ?? s.brand) || 'Competitor',
          series: xAxisLabels.map((label, index) => ({
            name: label,
            value: this.getSeriesByBrandMetricValue(s, index)
          }))
        }));
        return;
      }

      // Legacy format: x_axis + series[].data with type 'bar'/'line' (or no type: first series = bar, rest = line)
      const xAxis = competitorAnalysis.x_axis || [];
      let barSeries = competitorAnalysis.series.find((s: any) => s.type === 'bar');
      let lineSeriesList = competitorAnalysis.series.filter((s: any) => s.type === 'line');
      if (!barSeries && competitorAnalysis.series.length > 0) {
        barSeries = competitorAnalysis.series[0];
        if (lineSeriesList.length === 0 && competitorAnalysis.series.length > 1) {
          lineSeriesList = competitorAnalysis.series.slice(1);
        }
      }
      this.barSeriesBrandName = barSeries?.name ?? null;
      if (barSeries && barSeries.data) {
        this.results = xAxis.map((label: string, index: number) => ({
          name: label,
          value: barSeries.data[index] ?? 0
        }));
      } else {
        this.results = [];
      }
      if (lineSeriesList.length > 0) {
        this.lineChart = lineSeriesList.map((s: any) => ({
          name: s.name || 'Competitor',
          series: (xAxis as string[]).map((label: string, index: number) => ({
            name: label,
            value: (s.data && s.data[index]) ?? 0
          }))
        }));
      } else {
        this.lineChart = [];
      }
      return;
    }

    this.barSeriesBrandName = null;
    if (!d.performance_table || d.performance_table.length === 0) {
      this.results = [];
      this.lineChart = [];
      return;
    }

    // Find your brand (first brand or marked as your brand)
    const compositeBrand = d.composite_score?.brand;
    const yourBrand = compositeBrand
      ? d.performance_table.find((row: any) => row.brand === compositeBrand)
      : null;
    const yourBrandRow = yourBrand || d.performance_table[0];
    const competitorBrands = d.performance_table.filter((row: any) =>
      row.brand !== (compositeBrand || (d.performance_table[0]?.brand))
    );

    // Prepare bar chart data (results) - YOUR brand metrics as bars
    if (yourBrandRow) {
      this.results = [
        { name: 'Brand Reputation', value: yourBrandRow.brand_reputation || 0 },
        { name: 'Market Presence', value: yourBrandRow.market_presence || 0 },
        { name: 'Customer Satisfaction', value: yourBrandRow.customer_satisfaction || 0 },
        { name: 'Innovation Score', value: yourBrandRow.innovation_score || 0 },
        { name: 'Financial Performance', value: yourBrandRow.financial_performance || 0 },
        { name: 'Industry Influence', value: yourBrandRow.industry_influence || 0 }
      ];
    } else {
      this.results = [];
    }

    // Prepare line chart data (lineChart) - competitor brands as lines
    if (competitorBrands.length > 0) {
      this.lineChart = competitorBrands.map((brand: any) => ({
        name: brand.brand,
        series: [
          { name: 'Brand Reputation', value: brand.brand_reputation || 0 },
          { name: 'Market Presence', value: brand.market_presence || 0 },
          { name: 'Customer Satisfaction', value: brand.customer_satisfaction || 0 },
          { name: 'Innovation Score', value: brand.innovation_score || 0 },
          { name: 'Financial Performance', value: brand.financial_performance || 0 },
          { name: 'Industry Influence', value: brand.industry_influence || 0 }
        ]
      }));
    } else {
      this.lineChart = [];
    }
  }

  private prepareTableData(data: any): void {
    if (!data || !data.performance_table) {
      this.tableRows = [];
      return;
    }

    // Initialize table columns
    this.tableColumns = [
      { key: 'brand', header: 'Brand', className: 'brand-column' },
      { key: 'brand_reputation', header: 'Brand Reputation', className: 'metric-column' },
      { key: 'market_presence', header: 'Market Presence', className: 'metric-column' },
      { key: 'customer_satisfaction', header: 'Customer Satisfaction', className: 'metric-column' },
      { key: 'innovation_score', header: 'Innovation Score', className: 'metric-column' },
      { key: 'financial_performance', header: 'Financial Performance', className: 'metric-column' },
      { key: 'industry_influence', header: 'Industry Influence', className: 'metric-column' },
      { key: 'market_share', header: 'Market Share', className: 'metric-column' },
      { key: 'ranking', header: 'Ranking', className: 'metric-column' }
    ];

    // Prepare table rows
    this.tableRows = data.performance_table.map((row: any) => ({
      brand: row.brand,
      is_your_brand: row.is_your_brand || false,
      brand_reputation: row.brand_reputation || 0,
      market_presence: row.market_presence || 0,
      customer_satisfaction: row.customer_satisfaction || 0,
      innovation_score: row.innovation_score || 0,
      financial_performance: row.financial_performance || 0,
      industry_influence: row.industry_influence || 0,
      market_share: row.market_share || 'N/A',
      ranking: row.ranking || 'N/A'
    }));
  }

  // Chart event handlers
  onSelect(event: any): void {
    // console.log('Generic Overview chart item selected:', event);
  }

  onActivate(event: any): void {
    // console.log('Generic Overview combo chart activated:', event);
  }

  onDeactivate(event: any): void {
    // console.log('Generic Overview combo chart deactivated:', event);
  }

  // Axis formatting methods
  yLeftAxisScale(min: number, max: number): any {
    return { min: `${min}`, max: `${max}` };
  }

  yRightAxisScale(min: number, max: number): any {
    return { min: `${min}`, max: `${max}` };
  }

  yLeftTickFormat(data: number): string {
    return `${data.toLocaleString()}`;
  }

  yRightTickFormat(data: number): string {
    return `${data}%`;
  }

  /** Client name and website for Overview header and export (from current project). */
  get clientName(): string {
    const p = this.templateCommonService.getCurrentProjectData();
    return p?.name?.trim() || this.firstBrandName || 'Client';
  }
  get clientWebsite(): string {
    const p = this.templateCommonService.getCurrentProjectData();
    const d = p?.description?.trim();
    if (!d) return '';
    return d.startsWith('http') ? d : `https://${d}`;
  }

  /** AI Visibility Recommendations by timeframe (from overview data). */
  get aiVisibilityRecommendations(): { short_term: { title: string; description: string }[]; mid_term: { title: string; description: string }[]; long_term: { title: string; description: string }[] } {
    const raw = this.overviewData?.ai_visibility_recommendations ?? this.overviewData?.structured_data?.ai_visibility_recommendations;
    if (raw && typeof raw === 'object') {
      return {
        short_term: Array.isArray(raw.short_term) ? raw.short_term : [],
        mid_term: Array.isArray(raw.mid_term) ? raw.mid_term : [],
        long_term: Array.isArray(raw.long_term) ? raw.long_term : [],
      };
    }
    return { short_term: [], mid_term: [], long_term: [] };
  }

  /** Download Overview as PDF (as displayed; client name + website only in PDF). */
  async onDownloadOverview(): Promise<void> {
    const el = this.overviewPrintable?.nativeElement as HTMLElement;
    if (!el) return;
    const container = el.closest('.generic-overview-container') as HTMLElement;
    if (!container) return;
    try {
      container.classList.add('exporting-pdf');
      this.cdr.detectChanges();
      await new Promise(resolve => setTimeout(resolve, 150));
      const html2canvas = (await import('html2canvas')).default;
      const { jsPDF } = await import('jspdf');
      const canvas = await html2canvas(el, {
        scale: 2,
        useCORS: true,
        logging: false,
        backgroundColor: '#ffffff',
        imageTimeout: 0,
        // Skip favicon images to avoid hundreds of 404s and broken icons in PDF
        ignoreElements: (node) => {
          if (node.tagName === 'IMG') {
            const src = (node as HTMLImageElement).src || '';
            return src.includes('favicon') || src.includes('gstatic.com/favicon');
          }
          return false;
        },
      });
      container.classList.remove('exporting-pdf');
      this.cdr.detectChanges();

      const pdf = new jsPDF('p', 'mm', 'a4');
      const pageW = pdf.internal.pageSize.getWidth();
      const pageH = pdf.internal.pageSize.getHeight();
      const margin = 10;
      const contentW = pageW - 2 * margin;
      const contentH = pageH - 2 * margin;

      /** Full capture at readable width; height in mm if placed at contentW */
      const imgTotalH_mm = (canvas.height * contentW) / canvas.width;

      if (imgTotalH_mm <= contentH) {
        const imgData = canvas.toDataURL('image/jpeg', 0.92);
        pdf.addImage(imgData, 'JPEG', margin, margin, contentW, imgTotalH_mm);
      } else {
        /** Pixels of source canvas per PDF page (Semrush-style slice, no shrinking) */
        const pageHeightPx = Math.max(1, Math.floor((contentH / imgTotalH_mm) * canvas.height));
        let srcY = 0;
        let firstPage = true;
        while (srcY < canvas.height) {
          const slicePx = Math.min(pageHeightPx, canvas.height - srcY);
          const sliceCanvas = document.createElement('canvas');
          sliceCanvas.width = canvas.width;
          sliceCanvas.height = slicePx;
          const ctx = sliceCanvas.getContext('2d');
          if (!ctx) break;
          ctx.fillStyle = '#ffffff';
          ctx.fillRect(0, 0, sliceCanvas.width, sliceCanvas.height);
          ctx.drawImage(
            canvas,
            0,
            srcY,
            canvas.width,
            slicePx,
            0,
            0,
            canvas.width,
            slicePx
          );
          const imgData = sliceCanvas.toDataURL('image/jpeg', 0.92);
          const sliceH_mm = (slicePx * contentW) / canvas.width;
          if (!firstPage) pdf.addPage();
          pdf.addImage(imgData, 'JPEG', margin, margin, contentW, sliceH_mm);
          firstPage = false;
          srcY += slicePx;
        }
      }

      const name = this.clientName.replace(/[^a-z0-9-_]/gi, '_').slice(0, 40);
      pdf.save(`Overview_${name}.pdf`);
    } catch (e) {
      if (container) container.classList.remove('exporting-pdf');
      this.cdr.detectChanges();
      console.error('Overview PDF export failed:', e);
    }
    // window.location.reload();
  }

  // Get first brand name for chart (from composite_score, competitor_analysis bar series, or metadata)
  get firstBrandName(): string {
    return this.overviewData?.composite_score?.brand
      || this.barSeriesBrandName
      || this.overviewData?.metadata?.your_brand
      || 'Your Brand';
  }

  // Get citations from multiple possible locations
  getCitations(): any[] {
    if (!this.overviewData) return [];

    // Try top-level citations first (from API response)
    const topLevelCitations = (this.overviewData as any)?.citations;
    if (topLevelCitations && topLevelCitations.length > 0) {
      return topLevelCitations;
    }

    // Try structured_data.context.citations (new format)
    const contextCitations = (this.overviewData as any)?.context?.citations;
    if (contextCitations && contextCitations.length > 0) {
      return contextCitations;
    }

    // Try references.sources (old format)
    const referenceSources = this.overviewData?.references?.sources;
    if (referenceSources && referenceSources.length > 0) {
      return referenceSources;
    }

    return [];
  }

  /** Prompt/citation summary table from API: comparison_data[0].ai_visibility_metrics.table_prompt_citation_summary */
  get tablePromptCitationSummary(): any[] {
    const rows = this.llmComparisonData?.[0]?.ai_visibility_metrics?.table_prompt_citation_summary;
    return Array.isArray(rows) ? rows : [];
  }

  /** CSS class for change %: green if positive, red if negative (sign-based). */
  changePercentClass(changePercent: unknown): string {
    const n = Number(changePercent);
    if (!Number.isFinite(n)) return 'pct-zero';
    if (n > 0) return 'pct-pos';
    if (n < 0) return 'pct-neg';
    return 'pct-zero';
  }

  /** Formatted (±x%) for display */
  formatChangePercent(changePercent: unknown): string {
    const n = Number(changePercent);
    if (!Number.isFinite(n) || n === 0) return '(0%)';
    const sign = n > 0 ? '+' : '';
    return `(${sign}${n}%)`;
  }

  formatPercentCell(value: unknown): string {
    if (value === null || value === undefined || value === '') return '—';
    const n = Number(value);
    if (!Number.isFinite(n)) return '—';
    return `${Math.round(n)}%`;
  }

  // Extract LLM comparison data from overview response
  private extractLLMComparisonData(data: any): void {
    if (!data) {
      this.llmComparisonData = [];
      this.llmComparisonMetadata = null;
      return;
    }

    // Check for LLM comparison data in multiple possible locations
    let llmComparison = null;
    
    // Try root level first (most common - stored by backend in projectdata)
    if (data.llm_comparison) {
      llmComparison = data.llm_comparison;
      console.log('📦 Found LLM comparison data at root level (llm_comparison)');
    }
    // Try structured_data
    else if (data.structured_data?.llm_comparison) {
      llmComparison = data.structured_data.llm_comparison;
      console.log('📦 Found LLM comparison data in structured_data');
    }
    // Try direct access
    else if (data.comparison_data) {
      llmComparison = {
        comparison_data: data.comparison_data,
        metadata: data.metadata
      };
      console.log('📦 Found LLM comparison data as comparison_data');
    }

    if (llmComparison && llmComparison.comparison_data) {
      this.llmComparisonData = Array.isArray(llmComparison.comparison_data) 
        ? llmComparison.comparison_data 
        : [];
      this.llmComparisonMetadata = llmComparison.metadata || null;
      
      // Validate that we have actual data (not just empty array)
      if (this.llmComparisonData.length > 0) {
        const firstItem = this.llmComparisonData[0];
        const hasValidData = firstItem && (
          firstItem.ai_visibility_metrics || 
          firstItem.llm_metrics ||
          firstItem.brand
        );
        
        if (hasValidData) {
          console.log('✅ LLM Comparison data extracted and validated:', {
            count: this.llmComparisonData.length,
            firstBrand: firstItem.brand,
            hasAiVisibilityMetrics: !!firstItem.ai_visibility_metrics,
            hasLlmMetrics: !!firstItem.llm_metrics
          });
        } else {
          console.warn('⚠️ LLM comparison data found but appears to be empty/invalid');
          this.llmComparisonData = [];
          this.llmComparisonMetadata = null;
        }
      } else {
        console.warn('⚠️ LLM comparison data found but array is empty');
        this.llmComparisonData = [];
        this.llmComparisonMetadata = null;
      }
    } else {
      this.llmComparisonData = [];
      this.llmComparisonMetadata = null;
      console.log('⚠️ No LLM comparison data found. Available keys:', Object.keys(data || {}));
    }
  }

  /** Whether the AI Top Prompts section is expanded to show all cards with scroll. */
  topPromptsExpanded = false;

  /** Max prompts to show before "Expand" (initial view). */
  readonly topPromptsInitialCount = 8;

  // Top prompts by platform (all; UI shows first 8 then expand for rest with scroll)
  getTopPromptsByPlatform(): TopPromptByPlatformItem[] {
    if (!this.llmComparisonData?.length) return [];
    const primary = this.llmComparisonData[0];
    const breakdown = primary?.ai_visibility_metrics?.platform_breakdown;
    if (!breakdown) return [];
    const platformKeys: string[] = ['chatgpt', 'gemini', 'ai_overview', 'ai_mode'];
    const flat: TopPromptByPlatformItem[] = [];
    for (const platform of platformKeys) {
      const platData = breakdown[platform];
      const list = Array.isArray(platData?.query_results) ? platData.query_results : [];
      for (const row of list) {
        const query = row?.query ?? row?.prompt ?? '';
        const answer_text = row?.answer_text ?? row?.response ?? '';
        if (!query && !answer_text) continue;
        if (!String(answer_text).trim()) continue; // skip cards with empty response
        flat.push({
          platform,
          query,
          answer_text,
          mentioned_brands: Array.isArray(row?.mentioned_brands) ? row.mentioned_brands : [],
          citations: Array.isArray(row?.citations) ? row.citations : [],
          cited_domains: Array.isArray(row?.cited_domains) ? row.cited_domains : []
        });
      }
    }
    return flat;
  }

  /** Total citation count for a card (citations + cited_domains); used to sort so cards with multiple citations show first. */
  private citationCount(item: TopPromptByPlatformItem): number {
    const c = (item.citations?.length ?? 0) + (item.cited_domains?.length ?? 0);
    return c;
  }

  /** Same prompts as getTopPromptsByPlatform() but sorted so cards with the most citations appear at the top. */
  getTopPromptsInterleaved(): TopPromptByPlatformItem[] {
    const flat = this.getTopPromptsByPlatform();
    if (flat.length === 0) return [];
    return [...flat].sort((a, b) => this.citationCount(b) - this.citationCount(a));
  }

  /** Prompts to display: first 8 (highest citation count first) when collapsed, all when expanded. */
  getVisibleTopPrompts(): TopPromptByPlatformItem[] {
    const all = this.getTopPromptsInterleaved();
    if (this.topPromptsExpanded) return all;
    return all.slice(0, this.topPromptsInitialCount);
  }

  /** True when there are more than initial count and section is not expanded. */
  get hasMoreTopPrompts(): boolean {
    const all = this.getTopPromptsInterleaved();
    return !this.topPromptsExpanded && all.length > this.topPromptsInitialCount;
  }

  getPlatformLabel(platform: string): string {
    return PLATFORM_LABELS[platform] ?? platform;
  }

  // --- Top Domains table (mirrors AI Citation tab) ---
  private get firstAiVisibilityMetrics(): any {
    if (!this.llmComparisonData?.length) return null;
    return this.llmComparisonData[0]?.ai_visibility_metrics ?? null;
  }

  private get filteredDomainSummary(): any[] {
    const metrics = this.firstAiVisibilityMetrics;
    const summary = metrics?.domain_summary;
    return Array.isArray(summary) ? summary : [];
  }

  get simpleSourceTableData(): any[] {
    const summary = this.filteredDomainSummary;
    if (!summary.length) return [];
    const baseDomain = (this.templateCommonService.getCurrentFilterData()?.base_domain || '').trim().toLowerCase().replace(/^https?:\/\//, '').replace(/^www\./, '').replace(/\/.*$/, '');
    const capitalize = (s: string) => (s || 'other').toLowerCase() === 'ugc' ? 'UGC' : (s || 'other').charAt(0).toUpperCase() + (s || 'other').slice(1);
    const sorted = [...summary]
      .filter((d: any) => {
        const domain = String(d?.domain || '').trim().toLowerCase().replace(/^https?:\/\//, '').replace(/^www\./, '').replace(/\/.*$/, '');
        return !baseDomain || domain !== baseDomain;
      })
      .map((d: any) => ({
        source: d.domain || '',
        domain_type: capitalize(d.domain_type),
        used: Number(d.used_percent) ?? 0,
        avg_citations: Number(d.avg_citations) ?? 0
      }))
      .sort((a: any, b: any) => b.used - a.used);
    return sorted.slice(0, 6).map((d: any, i: number) => ({
      rank: i + 1,
      ...d,
      used_display: `${Math.round(d.used)}%`
    }));
  }

  get simpleSourceTableColumns(): TableColumn[] {
    return [
      { key: 'rank', label: '#', sortable: false, align: 'center', width: '5%' },
      { key: 'source', label: 'Source', sortable: true, align: 'left', width: '35%' },
      { key: 'domain_type', label: 'Domain Type', sortable: true, align: 'left', width: '20%' },
      { key: 'used_display', label: 'Used (%)', sortable: true, align: 'center', width: '15%' },
      { key: 'avg_citations', label: 'Avg. Citations', sortable: true, align: 'center', width: '25%' }
    ];
  }

  get simpleSourceTableConfig(): TableConfig {
    return { showHeader: true, showCard: true, enableSorting: true, enableHover: true };
  }

  get citedPagesForSelectedDomain(): any[] {
    if (!this.selectedDomainForPages) return [];
    const list = this.getCitedPagesList();
    if (!Array.isArray(list)) return [];
    const filtered = list.filter((p: any) => (p.domain || '') === this.selectedDomainForPages);
    const primaryBrand = this.firstBrandName?.trim().toLowerCase() || '';
    return filtered.map((p: any) => {
      const tm = p?.total_mentions;
      const brandMentioned = Array.isArray(tm) && tm.some((b: string) => String(b || '').trim().toLowerCase() === primaryBrand);
      const usedPercent = Number.isFinite(Number(p.used_percent)) ? Number(p.used_percent) : 0;
      const avgCit = Number.isFinite(Number(p.avg_citations)) ? Number(p.avg_citations) : 0;
      const otherBrands = Array.isArray(tm) ? tm.filter((b: string) => String(b || '').trim().toLowerCase() !== primaryBrand).map((b: string) => b?.trim()).filter(Boolean) : [];
      return {
        url: p.url || '',
        urlClickable: p.url || '',
        domain: p.domain || '',
        url_type: (p.url_type || 'other').toLowerCase() === 'ugc' ? 'UGC' : (p.url_type || 'other').charAt(0).toUpperCase() + (p.url_type || 'other').slice(1),
        brand_mentioned: brandMentioned ? 'Yes' : (p.brand_mentioned_on_page == null ? 'N/A' : 'No'),
        mentioned_brands: otherBrands.join(', ') || '—',
        mentions: p?.citation_count ?? (Array.isArray(tm) ? tm.length : 0),
        used_display: `${usedPercent}%`,
        avg_citations: avgCit,
        models: Array.isArray(p.model_sources) ? p.model_sources.join(', ') : ''
      };
    });
  }

  get simpleCitedPagesTableColumns(): TableColumn[] {
    const brandLabel = `${this.firstBrandName} mentioned`;
    return [
      { key: 'url', label: 'URL', sortable: false, align: 'left', width: '28%' },
      { key: 'url_type', label: 'URL Type', sortable: false, align: 'left', width: '10%' },
      { key: 'brand_mentioned', label: brandLabel, sortable: false, align: 'center', width: '10%' },
      { key: 'mentioned_brands', label: 'Mentions', sortable: false, align: 'left', width: '14%' },
      { key: 'mentions', label: 'Cited', sortable: true, align: 'center', width: '6%' },
      { key: 'used_display', label: 'Used (%)', sortable: true, align: 'center', width: '10%' },
      { key: 'avg_citations', label: 'Avg. Citations', sortable: true, align: 'center', width: '10%' }
    ];
  }

  get simpleCitedPagesTableConfig(): TableConfig {
    return { showHeader: true, showCard: true, enableSorting: true, enableHover: true };
  }

  get hasSimpleTopDomainsData(): boolean {
    return this.simpleSourceTableData.length > 0;
  }

  onSimpleSourceRowClick(row: any): void {
    const domain = row?.source ?? null;
    if (domain) {
      this.selectedDomainForPages = domain;
      this.cdr.markForCheck();
    }
  }

  clearSelectedDomainForPages(): void {
    this.selectedDomainForPages = null;
    this.cdr.markForCheck();
  }

  /** Domain favicon URL (same as AI-citations table). */
  getFaviconUrl(domain: string): string {
    if (!domain || typeof domain !== 'string') return '';
    const d = domain.trim().replace(/^https?:\/\//, '').split('/')[0].replace(/^www\./, '');
    return d ? `https://www.google.com/s2/favicons?domain=${encodeURIComponent(d)}&sz=32` : '';
  }

  /** First letter(s) for brand chip (same as AI-citations Mentions column). */
  getBrandInitial(name: string): string {
    if (!name || typeof name !== 'string') return '?';
    const t = name.trim();
    if (!t) return '?';
    const parts = t.split(/\s+/).filter(Boolean);
    if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase().slice(0, 2);
    return t[0].toUpperCase();
  }

  onFaviconError(event: Event): void {
    const el = event.target as HTMLImageElement;
    if (el) el.style.display = 'none';
  }

  /** Truncate answer for card snippet (~150 chars). */
  truncateAnswer(text: string, maxLen: number = 150): string {
    if (!text || text.length <= maxLen) return text ?? '';
    return text.slice(0, maxLen).trim() + '…';
  }

  openTopPromptDialog(item: TopPromptByPlatformItem): void {
    this.dialog.open(TopPromptByPlatformDialogComponent, {
      width: '80%',
      maxWidth: '95vw',
      maxHeight: '90vh',
      disableClose: true,
      data: { item },
      panelClass: 'top-prompt-by-platform-dialog'
    });
  }

  // Cited pages helpers
  getCitedPagesList(): any[] {
    if (!this.llmComparisonData || this.llmComparisonData.length === 0) {
      return [];
    }
    const primaryBrand = this.llmComparisonData[0];
    return primaryBrand?.ai_visibility_metrics?.cited_pages_list || [];
  }

  getFilteredCitedPages(): any[] {
    const pages = this.getCitedPagesList();
    if (!pages || pages.length === 0) {
      return [];
    }

    let filtered = pages;
    if (this.selectedModelFilter !== 'all') {
      const target = this.selectedModelFilter.toLowerCase();
      filtered = pages.filter(
        (p: any) =>
          Array.isArray(p.model_sources) &&
          p.model_sources.some((m: string) => m?.toLowerCase() === target)
      );
    }

    if (this.sortByDomain) {
      return [...filtered].sort((a, b) => {
        const domainA = (a.domain || '').toLowerCase();
        const domainB = (b.domain || '').toLowerCase();
        if (domainA === domainB) {
          const numA = a.citation_count ?? (Array.isArray(a.total_mentions) ? a.total_mentions.length : a.total_mentions) ?? 0;
          const numB = b.citation_count ?? (Array.isArray(b.total_mentions) ? b.total_mentions.length : b.total_mentions) ?? 0;
          return numB - numA;
        }
        return domainA.localeCompare(domainB);
      });
    }

    // Preserve backend default order (total_mentions DESC)
    return filtered;
  }

  getCitedPagesTableData(): any[] {
    return this.getFilteredCitedPages().map((page: any) => ({
      url: page.url,
      urlClickable: page.url, // Store original URL for click handler
      models: Array.isArray(page.model_sources) ? page.model_sources.join(', ') : '',
      domain: page.domain,
      total_mentions: page.citation_count ?? (Array.isArray(page.total_mentions) ? page.total_mentions.length : page.total_mentions) ?? 0
    }));
  }
  
  onUrlClick(url: string): void {
    if (url) {
      window.open(url, '_blank');
    }
  }

  // Helper methods to safely get metrics
  getChatGPTMetric(metric: 'ai_visibility' | 'mentions' | 'cited_pages'): number {
    if (!this.llmComparisonData || this.llmComparisonData.length === 0) {
      return 0;
    }
    const primaryBrand = this.llmComparisonData[0];
    const value = primaryBrand?.llm_metrics?.chatgpt?.[metric];
    console.log(`🔍 ChatGPT ${metric}:`, value, 'from', primaryBrand);
    return value || 0;
  }

  getGeminiMetric(metric: 'ai_visibility' | 'mentions' | 'cited_pages'): number {
    if (!this.llmComparisonData || this.llmComparisonData.length === 0) {
      return 0;
    }
    const primaryBrand = this.llmComparisonData[0];
    const value = primaryBrand?.llm_metrics?.gemini?.[metric];
    console.log(`🔍 Gemini ${metric}:`, value, 'from', primaryBrand);
    return value || 0;
  }

  // Calculate overall LLM metrics (sum across all brands and models)
  getOverallLLMMetrics(): { aiVisibility: number; mentions: number; citedPages: number } {
    if (!this.llmComparisonData || this.llmComparisonData.length === 0) {
      return { aiVisibility: 0, mentions: 0, citedPages: 0 };
    }

    let totalMentions = 0;
    let totalCitedPages = 0;
    let maxAiVisibility = 0;

    this.llmComparisonData.forEach((brandData: any) => {
      // ChatGPT metrics
      if (brandData.llm_metrics?.chatgpt) {
        totalMentions += brandData.llm_metrics.chatgpt.mentions || 0;
        totalCitedPages += brandData.llm_metrics.chatgpt.cited_pages || 0;
        maxAiVisibility = Math.max(maxAiVisibility, brandData.llm_metrics.chatgpt.ai_visibility || 0);
      }
      
      // Gemini metrics
      if (brandData.llm_metrics?.gemini) {
        totalMentions += brandData.llm_metrics.gemini.mentions || 0;
        totalCitedPages += brandData.llm_metrics.gemini.cited_pages || 0;
        maxAiVisibility = Math.max(maxAiVisibility, brandData.llm_metrics.gemini.ai_visibility || 0);
      }
    });

    return {
      aiVisibility: maxAiVisibility,
      mentions: totalMentions,
      citedPages: totalCitedPages
    };
  }

  private transformSourcesData(sources: any[]): any[] {
    return sources.map((source, index) => ({
      title: source.title || source.snippet || `Source ${index + 1}`,
      url: source.url,
      confidence: source.confidence || 0.7,
      snippet: source.snippet || source.title,
      source: source.source || "Tavily",
      category: 'overview',
      id: `source_${index}`
    }));
  }

  getEvidenceSources(): any[] {
    return this.evidenceSources || [];
  }

  // Handle filter change from AI Visibility component
  onCitedPagesFilterChange(filter: 'all' | 'chatgpt' | 'gemini' | 'ai_overview' | 'ai_mode'): void {
    this.selectedModelFilter = filter;
    this.cdr.detectChanges();
    
    // Scroll to table after a short delay to ensure DOM is updated
    setTimeout(() => {
      if (this.citedPagesTable) {
        this.citedPagesTable.nativeElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 100);
  }

  // Handle cancel LLM comparison metrics task
  onCancelLLMComparisonMetrics(taskId: string): void {
    this.templateCommonService.cancelLLMComparisonMetricsTask(taskId);
  }

  // Handle reload LLM comparison metrics
  onReloadLLMComparisonMetrics(): void {
    console.log('🔄 Reloading LLM comparison metrics...');
    this.showProgressBelowLoader = false; // Hide progress text below loader when reload clicked
    // Get current filter data
    const currentFilterData = this.templateCommonService.getCurrentFilterData();
    if (currentFilterData) {
      const { projectId, projectdataId } = this.templateCommonService.getCurrentProjectContext();
      console.log(`📦 Reloading with projectId: ${projectId || 'none'}, projectdataId: ${projectdataId || 'none'}`);
      this.templateCommonService.startLLMComparisonMetricsCalculation(
        currentFilterData,
        projectdataId || undefined,
        projectId || undefined
      );
    } else {
      console.warn('⚠️ No filter data available to reload LLM comparison metrics');
    }
  }
}
